import { useState } from 'react';
import { Button } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { last, map } from 'lodash';
import propTypes from 'prop-types';
import classnames from 'classnames';
import { parseTime } from 'utils/helpers/datetime';
import Calendar from 'components/calendar';
import { useDailyTimetableQuery } from 'redux/api_slice/service';
import {
  formatFullDateMini,
  formatQtDate,
  getPeriodList,
  getRouteIdBySegmentIds,
} from './helpers';

const FerryTimetable = ({ duration }) => {
  const availableOptions = useSelector((state) => {
    const result = [];
    const { service_routes: serviceRoutes, segments } = state.sealink;

    serviceRoutes.forEach((route) => {
      [route.forward_journey, route.return_journey].forEach((journey) => {
        result.push({
          name: journey.name,
          routeId: getRouteIdBySegmentIds(journey.segments, segments),
        });
      });
    });

    return result;
  });
  const [selectedOption, setSelectedOption] = useState(
    availableOptions[0].routeId
  );
  const [selectedDate, setSelectedDate] = useState(formatQtDate(new Date()));
  const [dateRangeList, setDateRangeList] = useState(
    getPeriodList(new Date(selectedDate))
  );
  const qtDates = Object.values(dateRangeList);
  const { data: timetableData, isFetching } = useDailyTimetableQuery({
    route_id: selectedOption,
    start_date: qtDates[0],
    end_date: last(qtDates),
  });

  return (
    <div>
      <div className="d-flex justify-content-center">
        <div className="border rounded p-1 text-center">
          {availableOptions.map(({ routeId, name }) => (
            <Button
              key={routeId}
              variant={routeId === selectedOption ? 'primary' : ''}
              onClick={() => setSelectedOption(routeId)}
            >
              {name}
            </Button>
          ))}
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-sm-center py-4 flex-sm-row flex-column-reverse">
        <div className="position-relative flex-grow-1 flex-basis-0 pt-3 pt-sm-0">
          <label htmlFor="date-select" className="text-primary" role="button">
            <i className="far fa-calendar-alt pe-1" />
            {formatFullDateMini(new Date(selectedDate))}
          </label>
          <Calendar
            inputProps={{
              id: 'date-select',
              className: 'visually-hidden start-0 bottom-0',
              placeholder: 'Please select a date',
            }}
            onChange={(event) => {
              setSelectedDate(formatQtDate(event.value));
              setDateRangeList(getPeriodList(event.value));
            }}
          />
        </div>
        <div className="text-center">
          {duration && (
            <>
              Approximate trip duration
              <h5 className="text-primary">{duration}</h5>
            </>
          )}
        </div>
        <div className="flex-grow-1 flex-basis-0" />
      </div>
      <div
        className={classnames('timetable', {
          'timetable--loading': isFetching,
        })}
      >
        <div className="timetable__container">
          <div className="timetable__row">
            {Object.keys(dateRangeList).map((date, index) => (
              <div
                key={date}
                className={classnames('timetable__col p-2', {
                  'text-primary': index === 0,
                })}
              >
                {date}
              </div>
            ))}
          </div>
          <div className="timetable__row">
            {isFetching ? (
              <>
                {Object.keys(dateRangeList).map((date) => (
                  <div key={date} className="timetable__col p-2">
                    <Skeleton count={5} height="2rem" className="mb-2" />
                  </div>
                ))}
              </>
            ) : (
              <>
                {map(timetableData, (column, key) => (
                  <div
                    key={key}
                    className={classnames('timetable__col', {
                      'd-flex justify-content-center align-items-center text-grey py-2':
                        !column.length,
                    })}
                  >
                    {column.length ? (
                      <>
                        {column.map((item) => (
                          <div key={item.id} className="timetable__item p-2">
                            {parseTime(item.departure_time)}
                          </div>
                        ))}
                      </>
                    ) : (
                      <>No Data</>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

FerryTimetable.propTypes = {
  duration: propTypes.string,
};

export default FerryTimetable;
